import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeApi, type KnowledgeArticle, type KnowledgeTopic } from '@/lib/api';
import {
  BookOpen, Plus, FileText, CheckCircle, Clock, ChevronRight, Trash2,
  ArrowLeft, Globe, FolderTree, ChevronDown, Pencil, X, Check, FolderPlus,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import styles from './_auth.knowledge.$sourceId.module.css';

export const Route = createFileRoute('/_auth/knowledge/$sourceId')({
  component: KnowledgeSourcePage,
});

// ── Country / language metadata ──────────────────────────────────────────────

const COUNTRY_NAMES: Record<string, string> = {
  FI: '🇫🇮 Finland', SE: '🇸🇪 Sweden', NO: '🇳🇴 Norway', DK: '🇩🇰 Denmark',
  GB: '🇬🇧 United Kingdom', US: '🇺🇸 United States', DE: '🇩🇪 Germany',
  FR: '🇫🇷 France', NL: '🇳🇱 Netherlands', ES: '🇪🇸 Spain', IT: '🇮🇹 Italy',
  PL: '🇵🇱 Poland', AU: '🇦🇺 Australia', CA: '🇨🇦 Canada',
};

const LANGUAGE_NAMES: Record<string, string> = {
  fi: 'Finnish', sv: 'Swedish', no: 'Norwegian', da: 'Danish',
  en: 'English', de: 'German', fr: 'French', nl: 'Dutch',
  es: 'Spanish', it: 'Italian', pl: 'Polish',
};

const COUNTRIES = Object.keys(COUNTRY_NAMES);
const LANGUAGES = Object.keys(LANGUAGE_NAMES);

function countryName(c: string | null) {
  if (!c) return '🌐 Global (all countries)';
  return COUNTRY_NAMES[c] ?? c;
}
function langName(l: string) { return LANGUAGE_NAMES[l] ?? l; }

export default function KnowledgeSourcePage() {
  const { sourceId } = Route.useParams();
  const matches = useMatches();

  // If a child route (article editor) is active, render it via Outlet
  const hasChildRoute = matches.some(m => m.routeId === '/_auth/knowledge/$sourceId/articles/$articleId');
  if (hasChildRoute) return <Outlet />;

  return <SourceListing sourceId={sourceId} />;
}

// ══════════════════════════════════════════════════════════════════════════════
// Source Listing — with topic tree sidebar
// ══════════════════════════════════════════════════════════════════════════════

function SourceListing({ sourceId }: { sourceId: string }) {
  const qc = useQueryClient();

  const { data: source } = useQuery({
    queryKey: ['knowledge-source', sourceId],
    queryFn: () => knowledgeApi.getSource(sourceId),
  });

  const { data: locales = [] } = useQuery({
    queryKey: ['knowledge-locales', sourceId],
    queryFn: () => knowledgeApi.listLocales(sourceId),
  });

  const { data: topics = [] } = useQuery({
    queryKey: ['knowledge-topics', sourceId],
    queryFn: () => knowledgeApi.listTopics(sourceId),
  });

  // Filters
  const [activeCountry, setActiveCountry] = useState<string | 'ALL'>('ALL');
  const [activeLang, setActiveLang] = useState<string | 'ALL'>('ALL');
  const [pubFilter, setPubFilter] = useState<'ALL' | 'PUBLISHED' | 'DRAFT'>('ALL');
  const [activeTopic, setActiveTopic] = useState<string | null>(null);

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['knowledge-articles', sourceId, activeCountry, activeLang, pubFilter, activeTopic],
    queryFn: () => knowledgeApi.listArticles(sourceId, {
      country: activeCountry === 'ALL' || activeCountry === 'GLOBAL' ? undefined : activeCountry,
      language: activeLang === 'ALL' ? undefined : activeLang,
      published: pubFilter === 'ALL' ? undefined : pubFilter === 'PUBLISHED',
      topicId: activeTopic ?? undefined,
    }),
  });

  // ── New article form state ──
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newCountry, setNewCountry] = useState<string>('');
  const [newLang, setNewLang] = useState('en');
  const [newTopicId, setNewTopicId] = useState<string>('');

  // ── New topic form state ──
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicParent, setNewTopicParent] = useState<string>('');

  // ── Delete confirmation ──
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeArticle | null>(null);

  const createMutation = useMutation({
    mutationFn: () => knowledgeApi.createArticle(sourceId, {
      title: newTitle,
      content: `# ${newTitle}\n\nStart writing here…`,
      country: newCountry || null,
      language: newLang,
      topicId: newTopicId || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] });
      qc.invalidateQueries({ queryKey: ['knowledge-locales', sourceId] });
      setShowNew(false);
      setNewTitle('');
    },
  });

  const createTopicMutation = useMutation({
    mutationFn: () => knowledgeApi.createTopic(sourceId, {
      name: newTopicName,
      parentId: newTopicParent || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-topics', sourceId] });
      setShowNewTopic(false);
      setNewTopicName('');
      setNewTopicParent('');
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.publish(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.deleteArticle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] });
      qc.invalidateQueries({ queryKey: ['knowledge-locales', sourceId] });
      setDeleteTarget(null);
    },
  });

  const publishedCount = (articles as KnowledgeArticle[]).filter(a => a.published).length;
  const draftCount = (articles as KnowledgeArticle[]).filter(a => !a.published).length;

  // Flatten topics for select dropdown
  function flattenTopics(nodes: KnowledgeTopic[], depth = 0): Array<{ id: string; name: string; depth: number }> {
    const result: Array<{ id: string; name: string; depth: number }> = [];
    for (const n of nodes) {
      result.push({ id: n.id, name: n.name, depth });
      if (n.children?.length) result.push(...flattenTopics(n.children, depth + 1));
    }
    return result;
  }
  const flatTopics = flattenTopics(topics as KnowledgeTopic[]);

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumb}>
        <Link to="/knowledge" className={styles.back}><ArrowLeft size={16} /> Knowledge Base</Link>
        <span className={styles.sep}>/</span>
        <span>{source?.name ?? '…'}</span>
      </div>

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><BookOpen size={22} /> {source?.name ?? 'Loading…'}</h1>
          {source?.description && <p className={styles.subtitle}>{source.description}</p>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className={styles.secondaryBtn} onClick={() => setShowNewTopic(true)}>
            <FolderPlus size={15} /> New Topic
          </button>
          <button className={styles.primaryBtn} onClick={() => setShowNew(true)}>
            <Plus size={16} /> New Article
          </button>
        </div>
      </div>

      {/* ── Locale matrix ── */}
      {locales.length > 0 && (
        <div className={styles.localeMatrix}>
          <div className={styles.matrixLabel}><Globe size={14} /> Locales in this source</div>
          <div className={styles.matrixGrid}>
            {locales.map(l => (
              <button
                key={`${l.country}::${l.language}`}
                className={`${styles.localeChip} ${
                  activeCountry === (l.country ?? 'GLOBAL') && activeLang === l.language ? styles.localeActive : ''
                }`}
                onClick={() => {
                  setActiveCountry(l.country ?? 'GLOBAL');
                  setActiveLang(l.language);
                }}
              >
                <span className={styles.localeCountry}>{countryName(l.country)}</span>
                <span className={styles.localeLang}>{langName(l.language)}</span>
                <span className={styles.localeCount}>{l.count}</span>
              </button>
            ))}
            <button
              className={`${styles.localeChip} ${activeCountry === 'ALL' ? styles.localeActive : ''}`}
              onClick={() => { setActiveCountry('ALL'); setActiveLang('ALL'); }}
            >
              <span className={styles.localeCountry}>🌐 All locales</span>
              <span className={styles.localeCount}>{locales.reduce((s, l) => s + l.count, 0)}</span>
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className={styles.stats}>
        <div className={styles.stat}><span className={styles.statNum}>{(articles as KnowledgeArticle[]).length}</span><span>Shown</span></div>
        <div className={styles.stat}><span className={`${styles.statNum} ${styles.published}`}>{publishedCount}</span><span>Published</span></div>
        <div className={styles.stat}><span className={`${styles.statNum} ${styles.draft}`}>{draftCount}</span><span>Drafts</span></div>
        <div className={styles.stat}><span className={styles.statNum}>{locales.length}</span><span>Locales</span></div>
      </div>

      {/* ── New topic form ── */}
      {showNewTopic && (
        <div className={styles.newArticleCard}>
          <h3><FolderTree size={16} /> New Topic</h3>
          <div className={styles.newGrid} style={{ gridTemplateColumns: '2fr 1fr' }}>
            <input
              className={styles.newTitle}
              autoFocus
              placeholder="Topic name…"
              value={newTopicName}
              onChange={e => setNewTopicName(e.target.value)}
              style={{ gridColumn: '1 / -1' }}
            />
            <label>
              <span>Parent Topic</span>
              <select value={newTopicParent} onChange={e => setNewTopicParent(e.target.value)}>
                <option value="">— Root (no parent) —</option>
                {flatTopics.map(t => (
                  <option key={t.id} value={t.id}>{'  '.repeat(t.depth)}{t.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className={styles.newActions}>
            <button className={styles.cancelBtn} onClick={() => setShowNewTopic(false)}>Cancel</button>
            <button
              className={styles.primaryBtn}
              onClick={() => createTopicMutation.mutate()}
              disabled={!newTopicName.trim() || createTopicMutation.isPending}
            >
              <Check size={14} /> Create Topic
            </button>
          </div>
        </div>
      )}

      {/* ── New article form ── */}
      {showNew && (
        <div className={styles.newArticleCard}>
          <h3>New Article</h3>
          <div className={styles.newGrid}>
            <input
              className={styles.newTitle}
              autoFocus
              placeholder="Article title…"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
            />
            <label>
              <span>Country</span>
              <select value={newCountry} onChange={e => setNewCountry(e.target.value)}>
                <option value="">🌐 Global (all countries)</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{COUNTRY_NAMES[c]}</option>)}
              </select>
            </label>
            <label>
              <span>Language</span>
              <select value={newLang} onChange={e => setNewLang(e.target.value)}>
                {LANGUAGES.map(l => <option key={l} value={l}>{LANGUAGE_NAMES[l]} ({l})</option>)}
              </select>
            </label>
            {flatTopics.length > 0 && (
              <label>
                <span>Topic</span>
                <select value={newTopicId} onChange={e => setNewTopicId(e.target.value)}>
                  <option value="">— No topic —</option>
                  {flatTopics.map(t => (
                    <option key={t.id} value={t.id}>{'  '.repeat(t.depth)}{t.name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {newCountry && newLang && (
            <div className={styles.localePreview}>
              Article will be scoped to: <strong>{countryName(newCountry)} — {langName(newLang)}</strong>
            </div>
          )}
          <div className={styles.newActions}>
            <button className={styles.cancelBtn} onClick={() => setShowNew(false)}>Cancel</button>
            <button className={styles.primaryBtn} onClick={() => createMutation.mutate()} disabled={!newTitle || createMutation.isPending}>
              Create & Edit
            </button>
          </div>
        </div>
      )}

      {/* ── Main content: sidebar + articles ── */}
      <div className={styles.splitLayout}>
        {/* ── Topic tree sidebar ── */}
        {(topics as KnowledgeTopic[]).length > 0 && (
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <FolderTree size={14} />
              <span>Topics</span>
            </div>
            <button
              className={`${styles.topicItem} ${activeTopic === null ? styles.topicActive : ''}`}
              onClick={() => setActiveTopic(null)}
            >
              <FileText size={13} />
              <span>All Articles</span>
            </button>
            {(topics as KnowledgeTopic[]).map(topic => (
              <TopicNode
                key={topic.id}
                topic={topic}
                depth={0}
                activeTopic={activeTopic}
                onSelect={setActiveTopic}
                sourceId={sourceId}
              />
            ))}
          </div>
        )}

        {/* ── Right: articles ── */}
        <div className={styles.content}>
          {/* ── Filter bar ── */}
          <div className={styles.filterBar}>
            <div className={styles.filters}>
              {(['ALL', 'PUBLISHED', 'DRAFT'] as const).map(f => (
                <button key={f} className={`${styles.filterBtn} ${pubFilter === f ? styles.active : ''}`} onClick={() => setPubFilter(f)}>
                  {f === 'ALL' ? 'All' : f === 'PUBLISHED' ? '✓ Published' : '⏱ Drafts'}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {activeTopic && (
                <div className={styles.activeLocale}>
                  Topic: {flatTopics.find(t => t.id === activeTopic)?.name ?? '…'}
                  <button onClick={() => setActiveTopic(null)}>✕</button>
                </div>
              )}
              {activeCountry !== 'ALL' && (
                <div className={styles.activeLocale}>
                  {countryName(activeCountry === 'GLOBAL' ? null : activeCountry)} / {langName(activeLang)}
                  <button onClick={() => { setActiveCountry('ALL'); setActiveLang('ALL'); }}>✕</button>
                </div>
              )}
            </div>
          </div>

          {/* ── Article list ── */}
          {isLoading ? (
            <div className={styles.loading}>Loading articles…</div>
          ) : (articles as KnowledgeArticle[]).length === 0 ? (
            <div className={styles.empty}>
              <FileText size={36} />
              <p>No articles for this filter. <button className={styles.linkBtn} onClick={() => setShowNew(true)}>Create one →</button></p>
            </div>
          ) : (
            <div className={styles.articleList}>
              {(articles as KnowledgeArticle[]).map(article => (
                <div key={article.id} className={styles.articleRow}>
                  <div className={styles.articleLeft}>
                    <div className={styles.articleStatus}>
                      {article.published
                        ? <CheckCircle size={15} className={styles.pubIcon} />
                        : <Clock size={15} className={styles.draftIcon} />}
                    </div>
                    <div className={styles.articleInfo}>
                      <Link
                        to="/knowledge/$sourceId/articles/$articleId"
                        params={{ sourceId, articleId: article.id }}
                        className={styles.articleTitle}
                      >
                        {article.title}
                      </Link>
                      <div className={styles.articleMeta}>
                        <span className={styles.localeBadge}>
                          {countryName(article.country)} · {langName(article.language)}
                        </span>
                        {article.topic && (
                          <span className={styles.topicBadge}>
                            <FolderTree size={10} /> {article.topic.name}
                          </span>
                        )}
                        {article._count?.chunks != null && (
                          <span className={styles.chunks}>{article._count.chunks} chunks</span>
                        )}
                        <span className={styles.updated}>Updated {new Date(article.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.articleActions}>
                    {!article.published && (
                      <button className={styles.publishBtn} onClick={() => publishMutation.mutate(article.id)}>
                        Publish
                      </button>
                    )}
                    <Link
                      to="/knowledge/$sourceId/articles/$articleId"
                      params={{ sourceId, articleId: article.id }}
                      className={styles.editBtn}
                    >
                      <ChevronRight size={16} /> Edit
                    </Link>
                    <button className={styles.deleteBtn} onClick={() => setDeleteTarget(article)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title={`Delete "${deleteTarget?.title}"?`}
        description="This will permanently remove the article and all its indexed chunks."
        confirmLabel="Delete Article"
        variant="danger"
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Topic Tree Node
// ══════════════════════════════════════════════════════════════════════════════

function TopicNode({
  topic,
  depth,
  activeTopic,
  onSelect,
  sourceId,
}: {
  topic: KnowledgeTopic;
  depth: number;
  activeTopic: string | null;
  onSelect: (id: string | null) => void;
  sourceId: string;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(topic.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const hasChildren = topic.children && topic.children.length > 0;
  const articleCount = (topic as unknown as { _count?: { articles: number } })._count?.articles ?? 0;

  const updateMutation = useMutation({
    mutationFn: () => knowledgeApi.updateTopic(topic.id, { name: editName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-topics', sourceId] });
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => knowledgeApi.deleteTopic(topic.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-topics', sourceId] });
      qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] });
      if (activeTopic === topic.id) onSelect(null);
      setConfirmDelete(false);
    },
  });

  return (
    <>
      <div
        className={`${styles.topicItem} ${activeTopic === topic.id ? styles.topicActive : ''}`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
      >
        {hasChildren ? (
          <button
            className={styles.topicExpand}
            onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            <ChevronDown size={12} style={{ transform: expanded ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
          </button>
        ) : (
          <span className={styles.topicDot} />
        )}

        {editing ? (
          <span className={styles.topicEditRow}>
            <input
              className={styles.topicEditInput}
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') updateMutation.mutate(); if (e.key === 'Escape') setEditing(false); }}
              autoFocus
            />
            <button className={styles.topicEditBtn} onClick={() => updateMutation.mutate()}><Check size={11} /></button>
            <button className={styles.topicEditBtn} onClick={() => setEditing(false)}><X size={11} /></button>
          </span>
        ) : (
          <>
            <button className={styles.topicLabel} onClick={() => onSelect(topic.id)}>
              {topic.name}
              {articleCount > 0 && <span className={styles.topicCount}>{articleCount}</span>}
            </button>
            <span className={styles.topicActions}>
              <button onClick={e => { e.stopPropagation(); setEditing(true); setEditName(topic.name); }}><Pencil size={11} /></button>
              <button onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}><Trash2 size={11} /></button>
            </span>
          </>
        )}
      </div>

      {expanded && hasChildren && (topic.children as KnowledgeTopic[]).map(child => (
        <TopicNode
          key={child.id}
          topic={child}
          depth={depth + 1}
          activeTopic={activeTopic}
          onSelect={onSelect}
          sourceId={sourceId}
        />
      ))}

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title={`Delete topic "${topic.name}"?`}
        description="Articles will be unassigned from this topic. Sub-topics will be moved to the parent."
        confirmLabel="Delete Topic"
        variant="danger"
      />
    </>
  );
}
